import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FavoritesContext = createContext(null);

export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(() => new Set());
  const [loading, setLoading] = useState(false);

  const isReader = !!(user && user.role === "reader");

  const refresh = useCallback(async () => {
    if (!isReader) {
      setIds(new Set());
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/my/favorites");
      setIds(new Set(Array.isArray(data) ? data : []));
    } catch {
      setIds(new Set());
    } finally {
      setLoading(false);
    }
  }, [isReader]);

  useEffect(() => { refresh(); }, [refresh]);

  const toggle = useCallback(
    async (bookId) => {
      if (!isReader) return { needsAuth: true };
      // optimistic
      const wasFavorited = ids.has(bookId);
      setIds((prev) => {
        const next = new Set(prev);
        if (wasFavorited) next.delete(bookId);
        else next.add(bookId);
        return next;
      });
      try {
        const { data } = await api.post(`/favorites/${bookId}/toggle`);
        // reconcile in case server disagrees
        setIds((prev) => {
          const next = new Set(prev);
          if (data.favorited) next.add(bookId);
          else next.delete(bookId);
          return next;
        });
        return { favorited: !!data.favorited };
      } catch (err) {
        // rollback
        setIds((prev) => {
          const next = new Set(prev);
          if (wasFavorited) next.add(bookId);
          else next.delete(bookId);
          return next;
        });
        return { error: err };
      }
    },
    [ids, isReader],
  );

  const isFavorited = useCallback((bookId) => ids.has(bookId), [ids]);

  return (
    <FavoritesContext.Provider
      value={{ ids, isFavorited, toggle, refresh, loading, canFavorite: isReader }}
    >
      {children}
    </FavoritesContext.Provider>
  );
}

export const useFavorites = () => useContext(FavoritesContext);
